import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Alert, Box, Button, CircularProgress, Typography } from "@mui/material";
import { useCartStore, type CartReconciliationReason } from "@ecommerce/cart";

interface CheckoutCartReconciliationGateProps {
    reason: Extract<CartReconciliationReason, "CHECKOUT_ENTER" | "CHECKOUT_RESUME">;
    children: ReactNode;
}

export const CheckoutCartReconciliationGate = ({
    reason,
    children,
}: CheckoutCartReconciliationGateProps) => {
    const [ready, setReady] = useState(false);
    const isReconciling = useCartStore((state) => state.isReconciling);
    const reconciliationError = useCartStore((state) => state.reconciliationError);
    const reconciliationChanges = useCartStore((state) => state.reconciliationChanges);
    const reconcilePrices = useCartStore((state) => state.reconcilePrices);

    const reconcile = useCallback(async () => {
        setReady(false);
        try {
            await reconcilePrices(reason);
            setReady(true);
        } catch {
            setReady(false);
        }
    }, [reason, reconcilePrices]);

    useEffect(() => {
        void reconcile();
    }, [reconcile]);

    if (!ready) {
        return (
            <Box
                sx={{
                    minHeight: "55vh",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2,
                    px: 2,
                }}
            >
                {isReconciling ? (
                    <>
                        <CircularProgress />
                        <Typography>Verificando precios, cantidades y disponibilidad…</Typography>
                    </>
                ) : (
                    <Alert
                        severity="error"
                        action={
                            <Button color="inherit" onClick={() => void reconcile()}>
                                Reintentar
                            </Button>
                        }
                    >
                        {reconciliationError ?? "No se pudieron verificar los precios del carrito."}
                    </Alert>
                )}
            </Box>
        );
    }

    return (
        <>
            {reconciliationChanges.length > 0 && (
                <Alert severity="warning" sx={{ m: 2 }}>
                    Actualizamos el carrito con los precios, cantidades y disponibilidad vigentes.
                    Revisá el resumen antes de continuar.
                </Alert>
            )}
            {children}
        </>
    );
};
